import { Injectable } from '@angular/core';
import { formatSelfServiceScaledNumber } from '../../utils/format.utils';
import { SelfServiceFormulaService } from './self-service-formula.service';
import {
  AggregationType,
  ChartShapeOptions,
  ChartStyle,
  CustomAggregation,
  NumberFormat,
  NumberScale,
  UploadedDataset,
} from './self-service-types';

/**
 * Shared, pure data + format helpers for the self-service feature.
 *
 * These were originally private methods on SelfServiceVisualizationComponent and
 * were used both by the chart-option builders AND by the component's KPI / table /
 * preview data-prep code. Extracting them here lets the chart-option-builder
 * service and the component depend on a single implementation instead of
 * duplicating logic. Everything is pure: state the methods need (shape options,
 * custom/builtin aggregations) is passed in explicitly rather than read off a
 * component field.
 */
@Injectable({ providedIn: 'root' })
export class SelfServiceDataUtilsService {
  static readonly PALETTES: Array<{ key: string; label: string; colors: string[] }> = [
    { key: 'default', label: 'Default', colors: ['#1f6feb', '#0f9f8f', '#8657d6', '#f59e0b', '#ef4444', '#10b981', '#3b82f6', '#ec4899'] },
    { key: 'warm', label: 'Warm', colors: ['#ef4444', '#f97316', '#f59e0b', '#eab308', '#dc2626', '#b45309', '#9333ea', '#c026d3'] },
    { key: 'cool', label: 'Cool', colors: ['#0ea5e9', '#06b6d4', '#0891b2', '#0284c7', '#2563eb', '#1d4ed8', '#4f46e5', '#7c3aed'] },
    { key: 'vibrant', label: 'Vibrant', colors: ['#ec4899', '#06b6d4', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#0ea5e9', '#84cc16'] },
    { key: 'monochrome', label: 'Monochrome', colors: ['#1e293b', '#334155', '#475569', '#64748b', '#94a3b8', '#cbd5e1', '#0f172a', '#020617'] },
  ];

  constructor(private readonly formulaService: SelfServiceFormulaService) {}

  getPaletteColors(key: string): string[] {
    return (SelfServiceDataUtilsService.PALETTES.find((p) => p.key === key) || SelfServiceDataUtilsService.PALETTES[0]).colors;
  }

  formatNumber(value: number, style: ChartStyle): string {
    return this.formatNumberWith(value, style.numberFormat, style.decimals, style.numberScale);
  }

  /** Format a value for the SECONDARY (right) Y-axis, using numberFormat2 /
   *  decimals2 when set and falling back to the primary format/decimals so
   *  existing charts (no secondary format) behave exactly as before. */
  formatNumberSecondary(value: number, style: ChartStyle): string {
    const fmt = style.numberFormat2 ?? style.numberFormat;
    const dec = style.decimals2 ?? style.decimals;
    const scale = style.numberScale2 ?? style.numberScale;
    return this.formatNumberWith(value, fmt, dec, scale);
  }

  /** Format a value for the TOOLTIP. Uses the relevant axis number format
   *  (primary, or secondary when `secondary` is true) but rounds to
   *  `style.tooltipDecimals` when set — so the hover tooltip can show a different
   *  precision from the axis labels. Falls back to the axis decimals otherwise.
   *
   *  Year-like values (a whole number in 1900–2100) are shown raw — a year is an
   *  identifier, not a measure, so applying thousands/percent/currency to it
   *  (e.g. "2,023" or "202300%") is wrong. */
  formatNumberTooltip(value: number, style: ChartStyle, secondary = false): string {
    if (this.isYearLike(value)) {
      return String(value);
    }
    const fmt = secondary ? (style.numberFormat2 ?? style.numberFormat) : style.numberFormat;
    const axisDec = secondary ? (style.decimals2 ?? style.decimals) : style.decimals;
    const dec = style.tooltipDecimals ?? axisDec;
    const scale = secondary ? (style.numberScale2 ?? style.numberScale) : style.numberScale;
    return this.formatNumberWith(value, fmt, dec, scale);
  }

  /** A whole number that falls in a plausible calendar-year range. Used to skip
   *  numeric formatting for years (which are identifiers, not measures). */
  isYearLike(value: number): boolean {
    return Number.isInteger(value) && value >= 1900 && value <= 2100;
  }

  /** True when EVERY non-blank value in `column` is a year-like number. Used to
   *  treat a year column as an identifier rather than a measure — so we don't
   *  sum years (2023 + 2023 = 4046) when it lands on a value axis. Returns false
   *  for an empty/blank column so we never misclassify "no data" as a year. */
  isYearColumn(dataset: UploadedDataset, columnName: string): boolean {
    if (!columnName) return false;
    let sawValue = false;
    for (const row of dataset.rows) {
      const raw = row[columnName];
      if (raw === undefined || raw === null || String(raw).trim() === '') continue;
      const num = Number(raw);
      if (!this.isYearLike(num)) return false;
      sawValue = true;
    }
    return sawValue;
  }

  /** Resolve the aggregation to actually use for a value column. A year column
   *  must NOT be summed/averaged/counted (that produces nonsense like 202300);
   *  collapse additive aggregations to `max` so the bar height is the real year
   *  (the latest, when a category spans multiple years). Non-additive choices the
   *  user picked (min/max/first/last) are left untouched. */
  resolveValueAggregation(
    dataset: UploadedDataset,
    columnName: string,
    aggregation: AggregationType,
  ): AggregationType {
    if (!this.isYearColumn(dataset, columnName)) return aggregation;
    const additive: AggregationType[] = ['sum', 'avg', 'count', 'distinctCount', 'distinct', 'median', 'range'];
    return additive.includes(aggregation) ? 'max' : aggregation;
  }

  /** Compact axis-label formatter. Axis TICK labels must stay short or they blow
   *  up the grid width (ECharts `containLabel` reserves space for the widest
   *  label INSIDE the grid, so a "300,000,000%" label collapses the plot into a
   *  thin strip — the reported "compressed chart, empty right side"). This
   *  abbreviates large magnitudes to K/M/B while preserving the axis's percent /
   *  currency intent, so tick labels stay ~4-6 chars regardless of the raw value.
   *  Tooltips keep full precision via `formatNumberTooltip` — only axis labels
   *  use this. */
  formatAxisLabel(value: number, style: ChartStyle, secondary = false): string {
    const fmt = secondary ? (style.numberFormat2 ?? style.numberFormat) : style.numberFormat;
    return this.formatCompact(value, fmt);
  }

  /** Abbreviate a number to at most a few characters (1.2K / 3.4M / 5B),
   *  carrying the percent (×100 + %) or currency ($) affordance of `fmt`. */
  private formatCompact(value: number, numberFormat: NumberFormat): string {
    if (!Number.isFinite(value)) return '—';

    // Percent axes multiply by 100; abbreviate the SCALED magnitude so the label
    // reflects what the axis actually shows.
    const scaled = numberFormat === 'percent' ? value * 100 : value;
    const abs = Math.abs(scaled);
    const prefix = numberFormat === 'currency' ? '$' : '';
    const suffix = numberFormat === 'percent' ? '%' : '';

    let body: string;
    if (abs >= 1e9) {
      body = this.trimZeros((scaled / 1e9).toFixed(1)) + 'B';
    } else if (abs >= 1e6) {
      body = this.trimZeros((scaled / 1e6).toFixed(1)) + 'M';
    } else if (abs >= 1e3) {
      body = this.trimZeros((scaled / 1e3).toFixed(1)) + 'K';
    } else {
      // Small values: keep up to one decimal so sub-thousand axes read cleanly.
      body = this.trimZeros(scaled.toFixed(Math.abs(scaled) < 10 && !Number.isInteger(scaled) ? 1 : 0));
    }
    return `${prefix}${body}${suffix}`;
  }

  private trimZeros(s: string): string {
    return s.replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
  }

  /** Core number formatter shared by the primary/secondary helpers. When a
   *  `numberScale` is chosen (K/M/B), the value is abbreviated with that scale
   *  instead of the format's normal rendering — the format still contributes
   *  its percent (×100 + %) or currency ($) affordance. */
  private formatNumberWith(
    value: number,
    numberFormat: NumberFormat,
    decimalsRaw: number | undefined,
    numberScale: NumberScale = 'none',
  ): string {
    if (!Number.isFinite(value)) {
      return '—';
    }
    const decimals = Math.max(0, Math.min(6, Math.floor(decimalsRaw || 0)));

    if (numberScale && numberScale !== 'none') {
      const isPercent = numberFormat === 'percent';
      const scaledForFormat = isPercent ? value * 100 : value;
      const prefix = numberFormat === 'currency' ? '$' : '';
      const suffix = isPercent ? '%' : '';
      return `${prefix}${formatSelfServiceScaledNumber(scaledForFormat, decimals, numberScale)}${suffix}`;
    }

    switch (numberFormat) {
      case 'thousands':
        return value.toLocaleString(undefined, {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        });
      case 'percent':
        return `${(value * 100).toLocaleString(undefined, {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        })}%`;
      case 'currency':
        return value.toLocaleString(undefined, {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        });
      default:
        return value.toFixed(decimals);
    }
  }

  toNumber(value: string | undefined): number {
    const normalized = String(value || '').replace(/,/g, '').trim();
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  applyDateGranularityToDataset(
    dataset: UploadedDataset,
    xKey: string,
    shapeOptions: ChartShapeOptions | null,
  ): UploadedDataset {
    const granularity = shapeOptions?.dateGranularity;
    if (!granularity || granularity === 'none' || !xKey) return dataset;
    const xColumn = dataset.columns.find((c) => c.name === xKey);
    if (!xColumn || xColumn.type !== 'date') return dataset;
    const bucket = (raw: string): string => {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return raw;
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      const day = d.getDate();
      switch (granularity) {
        case 'year': return String(y);
        case 'quarter': return `${y}-Q${Math.floor((m - 1) / 3) + 1}`;
        case 'month': return `${y}-${String(m).padStart(2, '0')}`;
        case 'week': {
          const start = new Date(d);
          start.setDate(d.getDate() - d.getDay());
          return `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
        }
        case 'day':
        default: return `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }
    };
    return {
      ...dataset,
      rows: dataset.rows.map((row) => ({ ...row, [xKey]: bucket(row[xKey] || '') })),
    };
  }

  applyShapeToAggregated(
    items: Array<{ name: string; value: number }>,
    shapeOptions: ChartShapeOptions | null,
  ): Array<{ name: string; value: number }> {
    const opts = shapeOptions;
    if (!opts) return items;
    let result = items;
    if (opts.sortBy) {
      const dir = opts.sortDir === 'desc' ? -1 : 1;
      result = [...result].sort((a, b) => {
        // sortBy === valueKey â†’ sort by value, else by name
        const useValue = opts.sortBy !== 'name' && (a.value !== undefined);
        if (useValue) return (a.value - b.value) * dir;
        return a.name.localeCompare(b.name) * dir;
      });
    }
    if (opts.topN && opts.topN > 0) {
      const sortedForLimit = opts.sortBy
        ? result
        : [...result].sort((a, b) => b.value - a.value);
      result = opts.topNDirection === 'bottom'
        ? sortedForLimit.slice(-opts.topN)
        : sortedForLimit.slice(0, opts.topN);
    }
    return result;
  }

  getCustomAggregation(key: string, customAggregations: CustomAggregation[]): CustomAggregation | undefined {
    return customAggregations.find((agg) => agg.key === key);
  }

  isCustomAggregation(
    value: AggregationType,
    builtinAggregations: Array<{ label: string; value: AggregationType }>,
  ): boolean {
    return !builtinAggregations.some((agg) => agg.value === value);
  }

  aggregateValues(values: number[], aggregation: AggregationType): number {
    if (aggregation === 'count') {
      return values.length;
    }
    if (aggregation === 'distinctCount' || aggregation === 'distinct') {
      return new Set(values).size;
    }
    if (!values.length) {
      return 0;
    }
    if (aggregation === 'avg') {
      return values.reduce((acc, value) => acc + value, 0) / values.length;
    }
    if (aggregation === 'min') {
      return Math.min(...values);
    }
    if (aggregation === 'max') {
      return Math.max(...values);
    }
    if (aggregation === 'range') {
      return Math.max(...values) - Math.min(...values);
    }
    if (aggregation === 'first') {
      return values[0];
    }
    if (aggregation === 'last') {
      return values[values.length - 1];
    }
    if (aggregation === 'median' || aggregation === 'p25' || aggregation === 'p75' || aggregation === 'p90' || aggregation === 'p95') {
      const percentile = aggregation === 'median' ? 50
        : aggregation === 'p25' ? 25
        : aggregation === 'p75' ? 75
        : aggregation === 'p90' ? 90
        : 95;
      const sorted = [...values].sort((a, b) => a - b);
      const rank = (percentile / 100) * (sorted.length - 1);
      const lo = Math.floor(rank);
      const hi = Math.ceil(rank);
      return lo === hi ? sorted[lo] : sorted[lo] + (sorted[hi] - sorted[lo]) * (rank - lo);
    }
    return values.reduce((acc, value) => acc + value, 0);
  }

  aggregateByCategory(
    dataset: UploadedDataset,
    categoryKey: string,
    valueKey: string,
    aggregation: AggregationType,
    shapeOptions: ChartShapeOptions | null,
    customAggregations: CustomAggregation[],
    builtinAggregations: Array<{ label: string; value: AggregationType }>,
  ): Array<{ name: string; value: number }> {
    dataset = this.applyDateGranularityToDataset(dataset, categoryKey, shapeOptions);
    const custom = this.isCustomAggregation(aggregation, builtinAggregations)
      ? this.getCustomAggregation(aggregation, customAggregations)
      : undefined;

    if (custom) {
      const groupedRows = new Map<string, Record<string, string>[]>();
      dataset.rows.forEach((row) => {
        const category = row[categoryKey] || '(blank)';
        const list = groupedRows.get(category) || [];
        list.push(row);
        groupedRows.set(category, list);
      });
      try {
        const evaluator = this.formulaService.buildAggregationEvaluator(custom.expression, dataset.columns.map((c) => c.name));
        return this.applyShapeToAggregated(
          Array.from(groupedRows.entries()).map(([name, rows]) => {
            const result = Number(evaluator(rows));
            return { name, value: Number.isFinite(result) ? result : 0 };
          }),
          shapeOptions,
        );
      } catch {
        return this.applyShapeToAggregated(
          Array.from(groupedRows.entries()).map(([name]) => ({ name, value: 0 })),
          shapeOptions,
        );
      }
    }

    const grouped = new Map<string, number[]>();
    dataset.rows.forEach((row) => {
      const category = row[categoryKey] || '(blank)';
      const values = grouped.get(category) || [];
      values.push(this.toNumber(row[valueKey]));
      grouped.set(category, values);
    });

    return this.applyShapeToAggregated(
      Array.from(grouped.entries()).map(([name, values]) => ({
        name,
        value: this.aggregateValues(values, aggregation),
      })),
      shapeOptions,
    );
  }

  aggregateByCategoryAndSeries(
    dataset: UploadedDataset,
    categoryKey: string,
    valueKey: string,
    seriesKey: string,
    aggregation: AggregationType,
    shapeOptions: ChartShapeOptions | null,
    customAggregations: CustomAggregation[],
    builtinAggregations: Array<{ label: string; value: AggregationType }>,
  ): { categories: string[]; seriesNames: string[]; data: number[][] } {
    const categories: string[] = [];
    const seriesNames: string[] = [];
    const categoryIndex = new Map<string, number>();
    const seriesIndex = new Map<string, number>();
    const buckets = new Map<string, Record<string, string>[]>();

    for (const row of dataset.rows) {
      const cat = row[categoryKey] || '(blank)';
      const ser = row[seriesKey] || '(blank)';
      if (!categoryIndex.has(cat)) {
        categoryIndex.set(cat, categories.length);
        categories.push(cat);
      }
      if (!seriesIndex.has(ser)) {
        seriesIndex.set(ser, seriesNames.length);
        seriesNames.push(ser);
      }
      // Bucket by category+series INDICES (not a joined string) so labels that
      // themselves contain the separator — e.g. "North America", "Q1 2023" —
      // don't get mis-split when we read the bucket back.
      const ci = categoryIndex.get(cat) ?? 0;
      const si = seriesIndex.get(ser) ?? 0;
      const bucketKey = `${ci}|${si}`;
      const list = buckets.get(bucketKey) || [];
      list.push(row);
      buckets.set(bucketKey, list);
    }

    const data: number[][] = categories.map(() => new Array(seriesNames.length).fill(0));
    for (const [bucketKey, rows] of buckets.entries()) {
      const [ciStr, siStr] = bucketKey.split('|');
      const ci = Number(ciStr);
      const si = Number(siStr);
      const aggregated = this.aggregateByCategory(
        { ...dataset, rows },
        categoryKey,
        valueKey,
        aggregation,
        shapeOptions,
        customAggregations,
        builtinAggregations,
      );
      data[ci][si] = aggregated[0]?.value ?? 0;
    }

    return { categories, seriesNames, data };
  }
}
